/**
 * cart-drawer.js
 *
 * Controls the slide-out cart drawer.
 *
 * Responsibilities:
 *  - Open / close (bag button, overlay click, X button, ESC key)
 *  - Focus trap while open
 *  - Body scroll lock via data-scroll-lock on <html>
 *  - AJAX qty stepper + remove via /cart/change.js → re-render drawer DOM
 *  - PDP add-to-cart intercept: POST /cart/add.js → open drawer
 *  - Keep bag-button badge count in sync after every cart mutation
 *  - Free-shipping progress bar (threshold from settings.free_shipping_threshold via data-shipping-threshold)
 *  - Conditional combo hint based on product handles in cart
 *
 * Progressive enhancement: if JS fails the form still posts to /cart/add normally.
 */

(function () {
  'use strict';

  const drawerEl = document.getElementById('cart-drawer');
  if (!drawerEl) return;

  const panelEl = drawerEl.querySelector('.cart-drawer__panel');
  const overlayEl = drawerEl.querySelector('[data-cart-drawer-overlay]');
  const closeBtn = drawerEl.querySelector('[data-cart-drawer-close]');
  const bagBtn = document.querySelector('[aria-controls="cart-drawer"]');
  const itemsContainer = drawerEl.querySelector('[data-cart-items]');
  const subtotalEl = drawerEl.querySelector('[data-cart-subtotal]');
  const badgeEl = document.getElementById('cart-count-badge');

  // ─── Free-shipping threshold (cents) ──────────────────────────────────────
  // Reads from data-shipping-threshold rendered by Liquid (settings.free_shipping_threshold × 100).

  const SHIPPING_THRESHOLD = parseInt(drawerEl.dataset.shippingThreshold, 10) || 40000;

  // ─── Open / close ──────────────────────────────────────────────────────────

  function openDrawer() {
    drawerEl.setAttribute('aria-hidden', 'false');
    drawerEl.classList.add('is-open');
    document.documentElement.setAttribute('data-scroll-lock', '');
    if (bagBtn) bagBtn.setAttribute('aria-expanded', 'true');
    // Shift focus to the panel for focus-trap baseline
    requestAnimationFrame(() => panelEl.focus());
  }

  function closeDrawer() {
    drawerEl.setAttribute('aria-hidden', 'true');
    drawerEl.classList.remove('is-open');
    document.documentElement.removeAttribute('data-scroll-lock');
    if (bagBtn) {
      bagBtn.setAttribute('aria-expanded', 'false');
      bagBtn.focus();
    }
  }

  function isOpen() {
    return drawerEl.classList.contains('is-open');
  }

  // ─── Focus trap ────────────────────────────────────────────────────────────

  function getFocusable() {
    return Array.from(
      panelEl.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.closest('[aria-hidden="true"]'));
  }

  drawerEl.addEventListener('keydown', (e) => {
    if (!isOpen()) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closeDrawer();
      return;
    }

    if (e.key === 'Tab') {
      const focusable = getFocusable();
      if (!focusable.length) { e.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
  });

  // ─── Trigger bindings ──────────────────────────────────────────────────────

  if (bagBtn) {
    bagBtn.addEventListener('click', () => {
      if (isOpen()) { closeDrawer(); return; }
      openDrawer();
      // Sync the whole drawer to the live cart on open — keeps items, subtotal and
      // the pack-matched upsell card consistent (e.g. if the cart changed in another tab).
      fetchCart().then((cart) => { if (cart) refreshDrawerFromCart(cart); }).catch(() => {});
    });
  }

  overlayEl.addEventListener('click', closeDrawer);
  closeBtn.addEventListener('click', closeDrawer);

  // ─── Badge helper ──────────────────────────────────────────────────────────

  function updateBadge(count) {
    if (!badgeEl) return;
    badgeEl.textContent = count;
    if (count > 0) {
      badgeEl.classList.remove('is-hidden');
      if (bagBtn) {
        bagBtn.setAttribute(
          'aria-label',
          `${bagBtn.getAttribute('aria-label').split('(')[0].trim()} (${count})`
        );
      }
    } else {
      badgeEl.classList.add('is-hidden');
    }
  }

  // ─── i18n strings from data-* attributes (set in cart-drawer.liquid via | t) ──

  const i18n = {
    empty: drawerEl.dataset.i18nEmpty || '',
    qtyLabel: drawerEl.dataset.i18nQtyLabel || '',
    decrease: drawerEl.dataset.i18nDecrease || '',
    increase: drawerEl.dataset.i18nIncrease || '',
    // Template: contains __TITLE__ placeholder — replaced per item
    removeTemplate: drawerEl.dataset.i18nRemove || '',
    // Template: contains __AMOUNT__ placeholder — replaced with ₪X
    shippingRemainingTemplate: drawerEl.dataset.i18nShippingRemaining || '',
    shippingFree: drawerEl.dataset.i18nShippingFree || '',
    comboRhAddPe: drawerEl.dataset.i18nComboRhAddPe || '',
    comboPeAddRh: drawerEl.dataset.i18nComboPeAddRh || '',
    comboBoth: drawerEl.dataset.i18nComboBoth || '',
    upsellDiscountLine: drawerEl.dataset.i18nUpsellDiscountLine || '',
    upsellAddButton: drawerEl.dataset.i18nUpsellAddButton || '',
    upsellChangePack: drawerEl.dataset.i18nUpsellChangePack || '',
  };

  // ─── Upsell product data (variant IDs + assets baked in at render time) ────

  // Parse a "pack::id~~pack::id~~…" map (built in cart-drawer.liquid) into an
  // ordered list + lookup so the upsell can match the pack already in the cart.
  function parsePackMap(raw) {
    const map = {};
    const order = [];
    (raw || '').split('~~').forEach((pair) => {
      const s = pair.trim();
      if (!s) return;
      const i = s.indexOf('::');
      if (i === -1) return;
      const pack = s.slice(0, i).trim();
      const id = s.slice(i + 2).trim();
      if (!pack || !id) return;
      if (!(pack in map)) order.push(pack);
      map[pack] = id;
    });
    return { map, order };
  }

  const rhPacks = parsePackMap(drawerEl.dataset.upsellRhPackMap);
  const pePacks = parsePackMap(drawerEl.dataset.upsellPePackMap);

  const upsell = {
    rh: {
      variantId: drawerEl.dataset.upsellRhVariantId || '',
      title: drawerEl.dataset.upsellRhTitle || '',
      img: drawerEl.dataset.upsellRhImg || '',
      packMap: rhPacks.map,
      packOrder: rhPacks.order,
    },
    pe: {
      variantId: drawerEl.dataset.upsellPeVariantId || '',
      title: drawerEl.dataset.upsellPeTitle || '',
      img: drawerEl.dataset.upsellPeImg || '',
      packMap: pePacks.map,
      packOrder: pePacks.order,
    },
    beeImg: (function () {
      // Derive bee-small.webp URL from the RH image URL (same CDN prefix)
      const ref = drawerEl.dataset.upsellRhImg || '';
      return ref ? ref.replace(/[^/]+$/, 'bee-small.webp') : '';
    })(),
  };

  function i18nRemove(title) {
    return i18n.removeTemplate.replace('__TITLE__', title);
  }

  function i18nShippingRemaining(amountStr) {
    return i18n.shippingRemainingTemplate.replace('__AMOUNT__', amountStr);
  }

  // ─── Money format — matches Liquid ₪{{ X | money_without_currency }} ───────
  // Prefixes ₪, 2 decimal places. Same format as PDP JS and all server-rendered prices.

  function moneyFormat(cents) {
    return '₪' + (cents / 100).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Returns just the formatted number (no ₪ prefix) — for the shipping hint
  // which uses the same ₪{{ X | money_without_currency }} pattern.
  function moneyWithoutCurrency(cents) {
    return (cents / 100).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // ─── Free-shipping bar update ───────────────────────────────────────────────

  function updateShippingBar(totalPriceCents) {
    const msgEl = drawerEl.querySelector('[data-cart-shipping-msg]');
    const fillEl = drawerEl.querySelector('[data-cart-shipping-fill]');
    if (!msgEl || !fillEl) return;

    const remaining = SHIPPING_THRESHOLD - totalPriceCents;
    if (remaining > 0) {
      const pct = Math.min(100, Math.round((totalPriceCents / SHIPPING_THRESHOLD) * 100));
      msgEl.textContent = i18nShippingRemaining(moneyWithoutCurrency(remaining));
      msgEl.classList.remove('cart-drawer__shipping-msg--done');
      fillEl.style.width = pct + '%';
      fillEl.classList.remove('cart-drawer__shipping-fill--done');
    } else {
      msgEl.textContent = i18n.shippingFree;
      msgEl.classList.add('cart-drawer__shipping-msg--done');
      fillEl.style.width = '100%';
      fillEl.classList.add('cart-drawer__shipping-fill--done');
    }
  }

  // ─── Combo hint + upsell card update ──────────────────────────────────────

  // Read the pack option value ("מארז 10") from a /cart.js line item
  function cartItemPack(item) {
    const ov = item && item.options_with_values;
    if (Array.isArray(ov)) {
      for (const o of ov) {
        if (o && typeof o.name === 'string' && o.name.indexOf('מארז') !== -1) return o.value;
      }
    }
    return null;
  }

  // Match the upsell to the pack the shopper already chose for the in-cart booklet
  function matchUpsellPack(cart, anchorHandle, product) {
    const order = product.packOrder || [];
    let matchedPack = '';
    if (cart && Array.isArray(cart.items)) {
      for (const item of cart.items) {
        if (item.handle !== anchorHandle) continue;
        const pack = cartItemPack(item);
        if (pack && product.packMap[pack]) { matchedPack = pack; break; }
        // Fallback: spot an upsell pack key inside the line's variant title
        const hay = (item.variant_title || '') + ' ' + (item.title || '');
        for (const k of order) {
          if (hay.indexOf(k) !== -1) { matchedPack = k; break; }
        }
        if (matchedPack) break;
      }
    }
    if (!matchedPack && order.length) matchedPack = order[0];
    const variantId = (matchedPack && product.packMap[matchedPack]) || product.variantId;
    return { matchedPack, variantId };
  }

  function buildUpsellCardHTML(product, matched) {
    const beeImg = '';
    const variantId = (matched && matched.variantId) || product.variantId;
    const matchedPack = (matched && matched.matchedPack) || '';
    const order = product.packOrder || [];

    const packLabel = matchedPack
      ? `<p class="cart-drawer__upsell-pack" data-upsell-pack-label><bdi>${escapeHTML(matchedPack)}</bdi></p>`
      : '';

    let toggle = '';
    let chips = '';
    if (order.length > 1) {
      toggle = `<button type="button" class="cart-drawer__upsell-change" data-upsell-change aria-expanded="false">${escapeHTML(i18n.upsellChangePack)}</button>`;
      const chipBtns = order.map((pack) => {
        const id = product.packMap[pack] || '';
        const active = pack === matchedPack ? ' is-active' : '';
        return `<button type="button" class="cart-drawer__upsell-chip${active}" data-upsell-pack data-pack-variant-id="${escapeAttr(id)}" data-pack-value="${escapeAttr(pack)}"><bdi>${escapeHTML(pack)}</bdi></button>`;
      }).join('');
      chips = `<div class="cart-drawer__upsell-chips" data-upsell-chips hidden>${chipBtns}</div>`;
    }

    return `
      <img src="${escapeAttr(product.img)}" alt="${escapeAttr(product.title)}" width="56" height="72" loading="lazy" class="cart-drawer__upsell-cover">
      <div class="cart-drawer__upsell-body">
        <p class="cart-drawer__upsell-title">${escapeHTML(product.title)}</p>
        ${packLabel}
        <p class="cart-drawer__upsell-discount">${escapeHTML(i18n.upsellDiscountLine)}</p>
        ${toggle}
        ${chips}
        <button type="button" class="cart-drawer__upsell-btn" data-upsell-add data-variant-id="${escapeAttr(variantId)}">${escapeHTML(i18n.upsellAddButton)}</button>
      </div>
      ${beeImg}
    `;
  }

  function updateComboHint(cart) {
    const hintEl = drawerEl.querySelector('[data-cart-combo-hint]');
    const cardEl = drawerEl.querySelector('[data-cart-upsell-card]');

    let hasRH = false;
    let hasPE = false;

    if (cart.item_count > 0) {
      for (const item of cart.items) {
        if (item.handle === 'rosh-hashana-blessings') hasRH = true;
        if (item.handle === 'passover-haggadah') hasPE = true;
      }
    }

    // Update text hint (both / empty states)
    if (hintEl) {
      hintEl.className = 'cart-drawer__combo-hint';
      hintEl.removeAttribute('aria-hidden');
      hintEl.textContent = '';

      if (hasRH && hasPE) {
        hintEl.textContent = i18n.comboBoth;
        hintEl.classList.add('cart-drawer__combo-hint--active');
      } else {
        hintEl.classList.add('is-hidden');
        hintEl.setAttribute('aria-hidden', 'true');
      }
    }

    // Update upsell card (exactly one booklet present)
    if (cardEl) {
      if (!hasRH && !hasPE || hasRH && hasPE || cart.item_count === 0) {
        cardEl.innerHTML = '';
        cardEl.classList.add('is-hidden');
        cardEl.setAttribute('aria-hidden', 'true');
      } else {
        const upsellPE = hasRH; // RH already in cart → upsell Passover (and vice-versa)
        const product = upsellPE ? upsell.pe : upsell.rh;
        const anchorHandle = upsellPE ? 'rosh-hashana-blessings' : 'passover-haggadah';
        const matched = matchUpsellPack(cart, anchorHandle, product);
        cardEl.innerHTML = buildUpsellCardHTML(product, matched);
        cardEl.classList.remove('is-hidden');
        cardEl.removeAttribute('aria-hidden');
      }
    }
  }

  // ─── Re-render drawer items from /cart.js ──────────────────────────────────

  function buildItemsHTML(cart) {
    if (cart.item_count === 0) {
      return `<p class="cart-drawer__empty">${escapeHTML(i18n.empty)}</p>`;
    }

    return cart.items
      .map((item) => {
        // Sanitize every string that touches the DOM template.
        // safeUrl enforces relative-path-only to block javascript: hrefs.
        const key = escapeAttr(String(item.key));
        const productHandle = escapeAttr(String(item.handle || ''));
        const productTitle = escapeHTML(item.product_title);
        const itemUrl = safeUrl(item.url);
        const quantity = parseInt(item.quantity, 10) || 0;

        const variantLine =
          item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-drawer__item-variant">${escapeHTML(item.variant_title)}</p>`
            : '';

        let imgTag = '';
        if (item.featured_image && item.featured_image.url) {
          // Shopify CDN URLs may or may not have existing query params — use the
          // correct separator so &width= doesn't corrupt a fresh URL.
          const rawUrl = item.featured_image.url;
          const sized = rawUrl.includes('?') ? rawUrl + '&width=120' : rawUrl + '?width=120';
          const imgSrc = safeUrl(sized);
          if (imgSrc) {
            imgTag = `<img src="${imgSrc}" alt="${escapeAttr(item.title)}" width="60" height="60" loading="lazy" class="cart-drawer__item-img">`;
          }
        }

        return `
          <div class="cart-drawer__item" data-item-key="${key}" data-product-handle="${productHandle}">
            <a href="${itemUrl}" class="cart-drawer__item-image-link" tabindex="-1" aria-hidden="true">
              ${imgTag}
            </a>
            <div class="cart-drawer__item-details">
              <a href="${itemUrl}" class="cart-drawer__item-title">${productTitle}</a>
              ${variantLine}
              <div class="cart-drawer__item-row">
                <div class="cart-drawer__qty" aria-label="${escapeAttr(i18n.qtyLabel)}" role="group">
                  <button type="button" class="cart-drawer__qty-btn" data-qty-change="${key}" data-qty-delta="-1" aria-label="${escapeAttr(i18n.decrease)}">−</button>
                  <span class="cart-drawer__qty-val" aria-live="polite">${quantity}</span>
                  <button type="button" class="cart-drawer__qty-btn" data-qty-change="${key}" data-qty-delta="1" aria-label="${escapeAttr(i18n.increase)}">+</button>
                </div>
                <span class="cart-drawer__item-price"><bdi>${escapeHTML(moneyFormat(item.final_line_price))}</bdi></span>
              </div>
            </div>
            <button type="button" class="cart-drawer__remove" data-remove-item="${key}" aria-label="${escapeAttr(i18nRemove(item.title))}">
              <svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 1L1 13M1 1l12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        `;
      })
      .join('');
  }

  /** Escape text content (between tags). */
  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;');
  }

  /** Escape attribute values (inside quotes). Reuses escapeHTML — all same chars matter. */
  function escapeAttr(str) {
    return escapeHTML(str);
  }

  /**
   * Allow only relative URLs and https:// CDN URLs (Shopify image CDN).
   * Returns '#' for anything that looks suspicious (javascript:, data:, etc.).
   */
  function safeUrl(url) {
    if (!url) return '#';
    const s = String(url).trim();
    // Relative paths and Shopify CDN are the only expected values
    if (s.startsWith('/') || s.startsWith('https://cdn.shopify.com')) {
      // Still escape for attribute context
      return escapeAttr(s);
    }
    return '#';
  }

  function refreshDrawerFromCart(cart) {
    if (itemsContainer) itemsContainer.innerHTML = buildItemsHTML(cart);
    // subtotalEl already contains a <bdi> from the server-rendered template;
    // target the text node inside it so we never touch innerHTML.
    if (subtotalEl) {
      const bdi = subtotalEl.querySelector('bdi') || subtotalEl;
      bdi.textContent = moneyFormat(cart.total_price);
    }
    updateBadge(cart.item_count);
    updateShippingBar(cart.total_price);
    updateComboHint(cart);
  }

  // ─── Fetch updated cart ─────────────────────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } }).then((r) => r.json());
  }

  // ─── AJAX cart change (qty step / remove) ──────────────────────────────────

  function cartChange(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    }).then((r) => r.json());
  }

  // Delegated listener on items container (re-created after each refresh)
  document.addEventListener('click', (e) => {
    // Qty delta buttons
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      const key = qtyBtn.dataset.qtyChange;
      const delta = parseInt(qtyBtn.dataset.qtyDelta, 10);
      const qtyValEl = qtyBtn.closest('.cart-drawer__qty').querySelector('.cart-drawer__qty-val');
      const current = parseInt(qtyValEl.textContent, 10);
      const newQty = Math.max(0, current + delta);
      cartChange(key, newQty).then((cart) => refreshDrawerFromCart(cart));
      return;
    }

    // Remove button
    const removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      const key = removeBtn.dataset.removeItem;
      cartChange(key, 0).then((cart) => refreshDrawerFromCart(cart));
      return;
    }

    // Upsell "change pack" toggle — reveal/hide the pack chips
    const changeBtn = e.target.closest('[data-upsell-change]');
    if (changeBtn) {
      const card = changeBtn.closest('[data-cart-upsell-card]');
      const chipsEl = card && card.querySelector('[data-upsell-chips]');
      if (chipsEl) {
        const willOpen = chipsEl.hasAttribute('hidden');
        if (willOpen) chipsEl.removeAttribute('hidden');
        else chipsEl.setAttribute('hidden', '');
        changeBtn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      }
      return;
    }

    // Upsell pack chip — re-point the add button to the chosen pack
    const chipBtn = e.target.closest('[data-upsell-pack]');
    if (chipBtn) {
      const card = chipBtn.closest('[data-cart-upsell-card]');
      if (!card) return;
      card.querySelectorAll('[data-upsell-pack]').forEach((c) => c.classList.remove('is-active'));
      chipBtn.classList.add('is-active');
      const id = chipBtn.dataset.packVariantId;
      const pack = chipBtn.dataset.packValue;
      const addBtn = card.querySelector('[data-upsell-add]');
      if (addBtn && id) addBtn.dataset.variantId = id;
      const lbl = card.querySelector('[data-upsell-pack-label] bdi');
      if (lbl && pack) lbl.textContent = pack;
      return;
    }

    // Upsell card add-to-cart button
    const upsellBtn = e.target.closest('[data-upsell-add]');
    if (upsellBtn) {
      const variantId = upsellBtn.dataset.variantId;
      if (!variantId) return;
      upsellBtn.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then((r) => r.json())
        .then(() => fetchCart())
        .then((cart) => refreshDrawerFromCart(cart))
        .catch(() => { upsellBtn.disabled = false; });
    }
  });

  // ─── PDP add-to-cart intercept ─────────────────────────────────────────────

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;

    e.preventDefault();
    const formData = new FormData(form);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData,
    })
      .then((r) => r.json())
      .then(() => fetchCart())
      .then((cart) => {
        refreshDrawerFromCart(cart);
        openDrawer();
      })
      .catch(() => {
        // JS failed — let the browser follow the form action normally
        form.submit();
      });
  });

})();
